import React, { useState, useMemo, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import { useNavigate } from 'react-router-dom';
import StickyScrollTable from '../../../../../components/common/StickyScrollTable';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { dateOnlyBodyTemplate } from '../../../../../utils/dateUtils';
import PdfGenerationService from '../../../../../utils/pdfGenerationService';
import { getOpeningPdfTranslations, getClosingPdfTranslations, mapClosingFormData } from '../../utils/pdfHelpers';
import RepairClosingExcelForm from '../../../../../utils/excel-forms/RepairClosingExcelForm';
import { getCustomerStatementHistoryColumns } from '../../constants/tableColumns';
import GenericHistoryDialog from '../../../../../components/common/GenericHistoryDialog';
import NewButton from '../../../../../components/NewButton/NewButton';
import {
    useGetCustomerStatementHistoryQuery,
    useLazyGetDeclarationByIdQuery,
    useLazyGetDeclarationReportByIdQuery,
    useMarkOpenDocumentPrintedMutation,
    useLazyGetWarrantClaimsForRepairCloseQuery,
    useLazyGetDraftWarrantyClaimsQuery
} from '../../../../../api/customerDeclarationApi';
import { useClientSideSort } from '../../hooks/useClientSideSort';
import ChassisInfoDialog from './chassis-info/ChassisInfoDialog';
import RepairCloseDialog from './RepairCloseDialog';
import DraftWarrantyClaimsDialog from './draft-claims/DraftWarrantyClaimsDialog';

const ActionButtons = React.memo(({ rowData, actions, loadingDraftClaims }) => (
    <div className="flex gap-2 align-items-center">
        <i onClick={() => actions.onEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={actions.tooltips.edit} />
        <i onClick={() => actions.onChassisInfo(rowData)} className="pi pi-info cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={actions.tooltips.chassisInfo} />
        <i onClick={() => actions.onHistory(rowData)} className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={actions.tooltips.history} />
        <i onClick={() => actions.onViewPdf(rowData.id)} className="pi pi-file-pdf text-blue-500 hover:text-blue-700 cursor-pointer" style={{ fontSize: '1.1rem' }} title={actions.tooltips.openingForm} />

        {/* Kapanış Formu (PDF/Excel format seçimi): status=10 ise görünür */}
        {rowData.status === 10 && (
            <i onClick={(e) => actions.onShowClosingFormatMenu(e, rowData)} className="pi pi-file-export text-red-500 hover:text-red-700 cursor-pointer" style={{ fontSize: '1.1rem' }} title={actions.tooltips.closingForm} />
        )}

        {/* Onarım Kapanış / Garanti Talep Detayları Butonu: status=10 ise görünür */}
        {rowData.status === 10 && (
            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <i
                    className={`pi pi-list cursor-pointer ${rowData.hasRepairCloseInvoice
                        ? 'text-purple-500 hover:text-purple-700'
                        : 'text-green-500 hover:text-green-700'
                        }`}
                    onClick={() => actions.onRepairClose(rowData)}
                    style={{ fontSize: '1.1rem' }}
                    title={
                        rowData.hasRepairCloseInvoice
                            ? (actions.tooltips.invoiced)
                            : actions.repairCloseTitle
                    }
                />
                {rowData.hasRepairCloseInvoice && (
                    <i
                        className="pi pi-check-circle text-purple-500"
                        style={{
                            fontSize: '0.6rem',
                            position: 'absolute',
                            top: '-4px',
                            right: '-6px'
                        }}
                    />
                )}
            </span>
        )}

        {/* YENİ: Taslak Garanti Talepleri butonu - status=5 ise görünür, status=10 ise gizli */}
        {rowData.status === 5 && (
            <i
                className={`pi ${loadingDraftClaims === rowData.id ? 'pi-spin pi-spinner' : 'pi-file-edit'} cursor-pointer text-orange-500 hover:text-orange-700`}
                onClick={() => !loadingDraftClaims && actions.onDraftClaims(rowData)}
                style={{
                    fontSize: '1.1rem',
                    opacity: loadingDraftClaims === rowData.id ? 0.6 : 1,
                    pointerEvents: loadingDraftClaims === rowData.id ? 'none' : 'auto'
                }}
                title={actions.tooltips.draftClaims}
            />
        )}

        {/* Talep Oluştur butonu: sadece status !== 10 ise görünür */}
        {actions.canCreateRequest && rowData.status !== 10 && (
            <i onClick={() => actions.onCreateRequest(rowData)} className="pi pi-plus cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={actions.tooltips.createWarrantyRequest} />
        )}
    </div>
));

ActionButtons.displayName = 'ActionButtons';

const CustomerDeclarationTable = ({ declarations, loading, totalCount, onPageChange, rows, first, handleNew, handleEdit }) => {
    const { t, getCurrentLanguageId } = useLanguage();
    const navigate = useNavigate();
    const toastRef = useRef(null);
    const { canCreateCustomerDeclaration } = usePermissions();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedChassis, setSelectedChassis] = useState(null);
    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [currentCustomerStatement, setCurrentCustomerStatement] = useState(null);

    const [repairCloseDialogVisible, setRepairCloseDialogVisible] = useState(false);
    const [repairCloseData, setRepairCloseData] = useState(null);

    const [draftClaimsDialogVisible, setDraftClaimsDialogVisible] = useState(false);
    const [selectedStatementForDraftClaims, setSelectedStatementForDraftClaims] = useState(null);
    const [draftClaimsData, setDraftClaimsData] = useState(null);
    const [loadingDraftClaims, setLoadingDraftClaims] = useState(null);

    const closingFormatMenuRef = useRef(null);
    const closingFormatTargetIdRef = useRef(null);

    const [triggerGetDeclarationById] = useLazyGetDeclarationByIdQuery();
    const [triggerGetDeclarationReportById] = useLazyGetDeclarationReportByIdQuery();
    const [markOpenDocumentPrinted] = useMarkOpenDocumentPrintedMutation();
    const [triggerGetWarrantClaimsForRepairClose] = useLazyGetWarrantClaimsForRepairCloseQuery();
    const [triggerGetDraftWarrantyClaims] = useLazyGetDraftWarrantyClaimsQuery();

    const pdfService = useMemo(() => new PdfGenerationService(), []);
    const canCreateWarrantyRequest = canCreateCustomerDeclaration();

    const { sortedData, sortField, sortOrder, onSort } = useClientSideSort(declarations || []);

    const handleViewPdf = useCallback(async (declarationId) => {
        try {
            const result = await triggerGetDeclarationById(declarationId).unwrap();
            if (result) {
                await markOpenDocumentPrinted(declarationId).unwrap();
                const translations = getOpeningPdfTranslations(t);
                const pdfDoc = pdfService.generateRepairOpeningFormPdf(result, translations);
                pdfService.viewPdf(pdfDoc);
            }
        } catch (error) {
            toastRef.current?.show({ severity: 'error', summary: t('common.error'), detail: t('warranty.customerDeclaration.messages.error'), life: 3000 });
        }
    }, [triggerGetDeclarationById, markOpenDocumentPrinted, pdfService, t]);

    const handleViewClosingFormPdf = useCallback(async (declarationId) => {
        try {
            const result = await triggerGetDeclarationReportById(declarationId).unwrap();
            if (result) {
                const pdfData = mapClosingFormData(result);
                const translations = getClosingPdfTranslations(t);
                const pdfDoc = pdfService.generateRepairClosingFormPdf(pdfData, translations, { halfPage: true });
                pdfService.viewPdf(pdfDoc);
            }
        } catch (error) {
            toastRef.current?.show({ severity: 'error', summary: t('common.error'), detail: t('warranty.customerDeclaration.messages.error'), life: 3000 });
        }
    }, [triggerGetDeclarationReportById, pdfService, t]);

    const handleDownloadClosingFormExcel = useCallback(async (declarationId) => {
        try {
            const result = await triggerGetDeclarationReportById(declarationId).unwrap();
            if (result) {
                const excelData = mapClosingFormData(result);
                const translations = getClosingPdfTranslations(t);
                const excelGenerator = new RepairClosingExcelForm();
                excelGenerator.generateAndDownload(excelData, translations, {
                    fileName: `RepairClosing_${declarationId}.xlsx`
                });
            }
        } catch (error) {
            toastRef.current?.show({ severity: 'error', summary: t('common.error'), detail: t('warranty.customerDeclaration.messages.error'), life: 3000 });
        }
    }, [triggerGetDeclarationReportById, t]);

    const closingFormatMenuItems = useMemo(() => [
        {
            label: 'PDF',
            icon: 'pi pi-file-pdf',
            command: () => {
                const id = closingFormatTargetIdRef.current;
                if (id) handleViewClosingFormPdf(id);
            }
        },
        {
            label: 'Excel',
            icon: 'pi pi-file-excel',
            command: () => {
                const id = closingFormatTargetIdRef.current;
                if (id) handleDownloadClosingFormExcel(id);
            }
        }
    ], [handleViewClosingFormPdf, handleDownloadClosingFormExcel]);

    const handleOpenRepairCloseDialog = useCallback(async (rowData) => {
        try {
            const languageId = getCurrentLanguageId();
            const result = await triggerGetWarrantClaimsForRepairClose({
                customerStatementId: rowData.id,
                languageId: languageId
            }).unwrap();

            if (result) {
                setRepairCloseData(result);
                setCurrentCustomerStatement(rowData);
                setRepairCloseDialogVisible(true);
            } else {
                toastRef.current?.show({
                    severity: 'error',
                    summary: t('common.error'),
                    detail: t('warranty.customerDeclaration.messages.error'),
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Garanti Talep Detayları yüklenirken hata:', error);
            const errorMessage = error?.data?.errorMessage || error?.data?.message || error?.message || t('warranty.customerDeclaration.messages.error');
            toastRef.current?.show({
                severity: 'error',
                summary: t('common.error'),
                detail: errorMessage,
                life: 3000
            });
        }
    }, [triggerGetWarrantClaimsForRepairClose, getCurrentLanguageId, t]);

    const handleCloseRepairCloseDialog = useCallback(() => {
        setRepairCloseDialogVisible(false);
        setRepairCloseData(null);
    }, []);

    // YENİ: Taslak Garanti Talepleri dialog aç - HER TIKLAMADA BACKEND'E İSTEK AT
    const handleOpenDraftClaimsDialog = useCallback(async (rowData) => {
        try {
            setLoadingDraftClaims(rowData.id); // Loading başlat

            const result = await triggerGetDraftWarrantyClaims(rowData.id).unwrap();

            if (result) {
                setDraftClaimsData(result);
                setSelectedStatementForDraftClaims(rowData);
                setDraftClaimsDialogVisible(true);
            } else {
                toastRef.current?.show({
                    severity: 'info',
                    summary: t('common.info'),
                    detail: t('warranty.customerDeclaration.draftClaims.emptyMessage'),
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Taslak garanti talepleri yüklenirken hata:', error);
            const errorMessage = error?.data?.errorMessage || error?.data?.message || error?.message || t('warranty.customerDeclaration.messages.error');
            toastRef.current?.show({
                severity: 'error',
                summary: t('common.error'),
                detail: errorMessage,
                life: 3000
            });
        } finally {
            setLoadingDraftClaims(null); // Loading bitir
        }
    }, [triggerGetDraftWarrantyClaims, t]);

    const handleCloseDraftClaimsDialog = useCallback(() => {
        setDraftClaimsDialogVisible(false);
        setSelectedStatementForDraftClaims(null);
        setDraftClaimsData(null); // Data'yı temizle
    }, []);

    const handleCreateWarrantyRequest = useCallback((rowData) => {
        navigate('/warranty-management/warranty-requests/claims/create', {
            state: {
                customerDeclarationId: rowData.id,
                chassisNumber: rowData.chassisNo,
                customerId: rowData.customer,
                claimDate: rowData.vehicleAcceptDate ? new Date(rowData.vehicleAcceptDate) : null,
                kmInformation: rowData.kmInformation || 0,
                customerDeclarationData: { id: rowData.id, chassisNumber: rowData.chassisNo }
            }
        });
    }, [navigate]);

    const actions = useMemo(() => ({
        onEdit: handleEdit,
        onChassisInfo: (row) => { setSelectedChassis(row.chassisNo); setDialogVisible(true); },
        onHistory: (row) => { setCurrentCustomerStatement(row); setHistoryDialogVisible(true); },
        onViewPdf: handleViewPdf,
        onShowClosingFormatMenu: (event, row) => {
            closingFormatTargetIdRef.current = row.id;
            closingFormatMenuRef.current?.toggle(event);
        },
        onRepairClose: handleOpenRepairCloseDialog,
        onDraftClaims: handleOpenDraftClaimsDialog,
        onCreateRequest: handleCreateWarrantyRequest,
        canCreateRequest: canCreateWarrantyRequest,
        repairCloseTitle: t('warranty.customerDeclaration.repairClose.title'),
        tooltips: {
            edit: t('warranty.customerDeclaration.tooltips.edit'),
            chassisInfo: t('warranty.customerDeclaration.tooltips.chassisInfo'),
            history: t('warranty.customerDeclaration.tooltips.history'),
            openingForm: t('warranty.customerDeclaration.tooltips.openingForm'),
            closingForm: t('warranty.customerDeclaration.tooltips.closingForm'),
            closingFormHalf: t('warranty.customerDeclaration.tooltips.closingFormHalf') || 'Yarım A4 Kapanış Formu',
            createWarrantyRequest: t('warranty.customerDeclaration.tooltips.createWarrantyRequest'),
            draftClaims: t('warranty.customerDeclaration.tooltips.draftClaims'),
            invoiced: t('warranty.customerDeclaration.tooltips.invoiced'),
        }
    }), [handleEdit, handleViewPdf, handleOpenRepairCloseDialog, handleOpenDraftClaimsDialog, handleCreateWarrantyRequest, canCreateWarrantyRequest, t]);

    const actionBodyTemplate = useCallback((rowData) => (
        <ActionButtons rowData={rowData} actions={actions} loadingDraftClaims={loadingDraftClaims} />
    ), [actions, loadingDraftClaims]);

    const statusBodyTemplate = (rowData) => {
        const statusMap = {
            5: { label: t('warranty.customerDeclaration.status.open'), severity: 'warning', icon: 'pi pi-clock' },
            10: { label: t('warranty.customerDeclaration.status.closed'), severity: 'success', icon: 'pi pi-check-circle' }
        };
        const status = statusMap[rowData.status] || { label: rowData.status, severity: 'info', icon: 'pi pi-info-circle' };

        return (
            <span
                className={`inline-flex align-items-center gap-1 px-2 py-1 border-round text-${status.severity}-700 bg-${status.severity}-100`}
                style={{ fontSize: '0.875rem', fontWeight: 500 }}
            >
                <i className={status.icon} style={{ fontSize: '0.75rem' }}></i>
                {status.label}
            </span>
        );
    };

    const handleSortChange = (e) => {
        onSort(e);
    };

    return (
        <>
            <Toast ref={toastRef} />
            <Menu model={closingFormatMenuItems} popup ref={closingFormatMenuRef} />
            <GenericHistoryDialog
                hideDialog={() => setHistoryDialogVisible(false)}
                dialogVisible={historyDialogVisible}
                entity={currentCustomerStatement}
                entityType="customerStatement"
                useHistoryQuery={useGetCustomerStatementHistoryQuery}
                columns={getCustomerStatementHistoryColumns(t)}
                titleKey="customerDefinition.history.title"
                entityIdentifierField="id"
            />

            <StickyScrollTable deps={[sortedData]}>
                <DataTable
                    value={sortedData}
                    paginator
                    lazy
                    totalRecords={totalCount}
                    loading={loading}
                    rows={rows}
                    first={first}
                    onPage={onPageChange}
                    onSort={handleSortChange}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    dataKey="id"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="{first} - {last} / {totalRecords}"
                    className="p-datatable-gridlines"
                    pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
                    header={
                        canCreateCustomerDeclaration() && (
                            <div className="flex justify-content-end">
                                <NewButton label={t('warranty.customerDeclaration.dialog.buttons.addNew')} onClick={handleNew} />
                            </div>
                        )
                    }
                >
                    <Column body={actionBodyTemplate} style={{ minWidth: '160px' }} headerClassName="text-center" bodyClassName="text-center" />
                    <Column field="bosBranchCode" header={t('warranty.customerDeclaration.table.dealerCode')} sortable />
                    <Column field="boshBranchName" header={t('warranty.customerDeclaration.table.dealer')} sortable />
                    <Column field="customerCountry" header={t('warranty.customerDeclaration.table.country')} sortable />
                    <Column field="id" header={t('warranty.customerDeclaration.table.workOrderNo')} sortable />
                    <Column field="status" header={t('warranty.customerDeclaration.table.status')} body={statusBodyTemplate} sortable />
                    <Column field="chassisNo" header={t('warranty.customerDeclaration.table.chassisNo')} sortable />
                    <Column field="licensePlate" header={t('warranty.customerDeclaration.table.licensePlate')} sortable />
                    <Column field="vehicleAcceptDate" header={t('warranty.customerDeclaration.table.faultDate')} body={(row) => dateOnlyBodyTemplate(row, 'vehicleAcceptDate')} sortable />
                    <Column field="receivingPerson" header={t('warranty.customerDeclaration.table.deliveredBy')} sortable />
                    <Column field="receivingDeliveryPerson" header={t('warranty.customerDeclaration.table.receivedBy')} sortable />
                    <Column field="customerNameSurname" header={t('warranty.customerDeclaration.table.customer')} sortable />
                    <Column field="kmInformation" header={t('warranty.customerDeclaration.table.kmInformation')} sortable />
                </DataTable>
            </StickyScrollTable>

            <ChassisInfoDialog visible={dialogVisible} onHide={() => setDialogVisible(false)} chassisNo={selectedChassis} />

            <RepairCloseDialog
                visible={repairCloseDialogVisible}
                onHide={handleCloseRepairCloseDialog}
                data={repairCloseData}
                customerStatementId={currentCustomerStatement?.id}
                isDomestic={currentCustomerStatement?.isDomestic === true}
            />

            <DraftWarrantyClaimsDialog
                visible={draftClaimsDialogVisible}
                onHide={handleCloseDraftClaimsDialog}
                customerStatementId={selectedStatementForDraftClaims?.id}
                data={draftClaimsData}
            />
        </>
    );
};

export default React.memo(CustomerDeclarationTable);
